import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Home,
  Calendar,
  MapPin,
  Building,
  Search,
  Star,
  Clock,
  Send,
  CheckCircle,
  Mail,
  MailOpen,
  ChevronLeft,
  ChevronRight,
  Eye,
  ExternalLink,
} from 'lucide-react';

import { AppHeader, AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const FRESHNESS_LABELS = {
  quarter: 'laatste 3 maanden',
  month: 'laatste maand',
  week: 'laatste week',
  all: 'alle periodes',
};

const DEFAULT_PROVINCES = [
  { value: 'all', label: 'Alle provincies' },
  { value: 'Antwerpen', label: 'Antwerpen' },
  { value: 'Limburg', label: 'Limburg' },
  { value: 'Oost-Vlaanderen', label: 'Oost-Vlaanderen' },
  { value: 'Vlaams-Brabant', label: 'Vlaams-Brabant' },
  { value: 'West-Vlaanderen', label: 'West-Vlaanderen' },
];

const STATUS_CONFIG = {
  new: { label: 'Nieuw', variant: 'default', icon: Clock },
  interessant: { label: 'Favorieten', variant: 'secondary', icon: Star },
  gelezen: { label: 'Gelezen', variant: 'outline', icon: Eye },
  gecontacteerd: { label: 'Gecontacteerd', variant: 'default', icon: CheckCircle },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const formatArea = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 'Onbekend';
  }
  return `${Math.round(value).toLocaleString('nl-BE')} m²`;
};

const LeafletMap = ({ geometry }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const geoLayerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return () => {};
    }

    if (!mapRef.current) {
      const map = L.map(container, {
        zoomControl: false,
        attributionControl: false,
      });

      L.control.attribution({ prefix: false }).addTo(map).setPrefix('© Digitaal Vlaanderen - GRB');

      L.tileLayer.wms('https://geo.api.vlaanderen.be/GRB/wms', {
        layers: 'GRB_BSK',
        format: 'image/png',
        transparent: false,
        version: '1.3.0',
      }).addTo(map);

      L.tileLayer.wms('https://geo.api.vlaanderen.be/GRB/wms', {
        layers: 'GRB_ADP_GRENS',
        format: 'image/png',
        transparent: true,
        version: '1.3.0',
      }).addTo(map);

      mapRef.current = map;
    } else {
      mapRef.current.invalidateSize();
    }

    return () => {
      if (!mapRef.current) {
        return;
      }
      mapRef.current.remove();
      mapRef.current = null;
      geoLayerRef.current = null;
      if (container) {
        container._leaflet_id = undefined;
        container.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;

    if (!geoLayerRef.current) {
      geoLayerRef.current = L.geoJSON(null, {
        style: {
          color: '#2563eb',
          weight: 2,
          fillColor: '#3b82f6',
          fillOpacity: 0.25,
        },
        pointToLayer: (_feature, latlng) =>
          L.circleMarker(latlng, {
            radius: 8,
            color: '#2563eb',
            weight: 2,
            fillColor: '#3b82f6',
            fillOpacity: 0.7,
          }),
      }).addTo(map);
    }

    const layer = geoLayerRef.current;
    layer.clearLayers();

    if (!geometry) {
      map.invalidateSize();
      return;
    }

    layer.addData(geometry);

    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [16, 16] });
    } else if (geometry?.type === 'Point' || geometry?.geometry?.type === 'Point') {
      const point = geometry.geometry?.type === 'Point' ? geometry.geometry : geometry;
      const [lon, lat] = point.coordinates;
      map.setView([lat, lon], 18);
    }

    layer.bringToFront();
    map.invalidateSize();
  }, [geometry]);

  return <div ref={containerRef} className="h-full w-full" />;
};

const OOWoningenModule = () => {
  const { user, logout, token } = useContext(AuthContext);

  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [selectedGemeente, setSelectedGemeente] = useState('all');
  const [selectedPostcode, setSelectedPostcode] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [freshnessFilter, setFreshnessFilter] = useState('quarter');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [activeStatsTab, setActiveStatsTab] = useState(null);  // Track which stats card is active: 'total', 'campagne', 'favorieten', 'verzonden'
  const PAGE_SIZE = 500;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusValue, setStatusValue] = useState('new');

  const handleNavigate = (path) => {
    window.location.href = path;
  };

  const provinces = useMemo(() => {
    if (!stats?.province_counts || stats.province_counts.length === 0) {
      return DEFAULT_PROVINCES;
    }
    const dynamic = stats.province_counts.map((item) => ({
      value: item.province,
      label: item.province,
    }));
    return [{ value: 'all', label: 'Alle provincies' }, ...dynamic];
  }, [stats]);

  const gemeenten = useMemo(() => {
    const values = new Set();
    listings.forEach((listing) => {
      if (listing.gemeente) {
        values.add(listing.gemeente);
      }
    });
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [listings]);

  const fetchStats = async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
      if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
      if (selectedPostcode) params.set('postcode', selectedPostcode);
      if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
      if (showFavoritesOnly) params.set('favorites_only', 'true');
      if (searchTerm) params.set('search', searchTerm);
      if (freshnessFilter) params.set('freshness', freshnessFilter);

      const response = await axios.get(`${API}/oo/stats?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStats(response.data);
      if (typeof response.data?.total === 'number') {
        setTotalCount(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch OO stats:', err);
      setError('Kon statistieken niet laden');
    }
  };

  const fetchListings = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Apply stats tab filters first (these override other filters)
      if (activeStatsTab === 'total') {
        // Total woningen: show all woningen, any status (no status filter)
        // Only apply province, gemeente, postcode filters if set
        if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
        if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
        if (selectedPostcode) params.set('postcode', selectedPostcode);
      } else if (activeStatsTab === 'campagne') {
        // Campagne: only woningen with favorite=true
        params.set('favorites_only', 'true');
        // Still allow province, gemeente, postcode filters
        if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
        if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
        if (selectedPostcode) params.set('postcode', selectedPostcode);
      } else if (activeStatsTab === 'favorieten') {
        // Favorieten: woningen with status "interessant"
        params.set('status', 'interessant');
        // Still allow province, gemeente, postcode filters
        if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
        if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
        if (selectedPostcode) params.set('postcode', selectedPostcode);
      } else if (activeStatsTab === 'verzonden') {
        // Verzonden capagnes: woningen with status "gecontacteerd"
        params.set('status', 'gecontacteerd');
        // Still allow province, gemeente, postcode filters
        if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
        if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
        if (selectedPostcode) params.set('postcode', selectedPostcode);
      } else {
        // No stats tab active: apply all normal filters
        if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
        if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
        if (selectedPostcode) params.set('postcode', selectedPostcode);
        if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
        if (showFavoritesOnly) params.set('favorites_only', 'true');
      }
      
      if (searchTerm) params.set('search', searchTerm);
      if (freshnessFilter) params.set('freshness', freshnessFilter);
      params.set('skip', (currentPage * PAGE_SIZE).toString());
      params.set('limit', PAGE_SIZE.toString());

      const response = await axios.get(`${API}/oo/listings?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setListings(items);
      if (typeof response.data?.total === 'number') {
        setTotalCount(response.data.total);
      } else {
        setTotalCount(items.length);
      }
      setError('');
    } catch (err) {
      console.error('Failed to fetch OO listings:', err);
      setListings([]);
      setTotalCount(0);
      setError('Kon woningen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchStats();
  }, [token, selectedProvince, selectedGemeente, selectedPostcode, selectedStatus, showFavoritesOnly, searchTerm, freshnessFilter, activeStatsTab]);

  useEffect(() => {
    fetchListings();
  }, [selectedProvince, selectedGemeente, selectedPostcode, selectedStatus, showFavoritesOnly, searchTerm, freshnessFilter, currentPage, token, activeStatsTab]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedProvince, selectedGemeente, selectedPostcode, selectedStatus, showFavoritesOnly, searchTerm, freshnessFilter, activeStatsTab]);

  const markListingAsRead = async (listing) => {
    if (!listing || listing.status !== 'new' || !token) {
      return;
    }
    try {
      await axios.patch(
        `${API}/oo/listings/${listing.poiid}`,
        { status: 'gelezen' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setListings((prev) =>
        prev.map((item) =>
          item.poiid === listing.poiid ? { ...item, status: 'gelezen' } : item,
        ),
      );
      setSelectedListing((prev) =>
        prev && prev.poiid === listing.poiid ? { ...prev, status: 'gelezen' } : prev,
      );
      setStatusValue('gelezen');
      fetchStats();
    } catch (err) {
      console.error('Failed to mark listing as gelezen:', err);
    }
  };

  const handleViewDetails = (listing) => {
    setSelectedListing(listing);
    setStatusValue(listing.status || 'new');
    setSheetOpen(true);
    markListingAsRead(listing);
  };

  const handleToggleFavorite = async (listing, isFavorite) => {
    try {
      await axios.patch(`${API}/oo/listings/${listing.poiid}`, { is_favorite: isFavorite }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setListings((prev) =>
        prev.map((item) => (item.poiid === listing.poiid ? { ...item, is_favorite: isFavorite } : item)),
      );
      if (selectedListing?.poiid === listing.poiid) {
        setSelectedListing({ ...selectedListing, is_favorite: isFavorite });
      }
      fetchStats();
    } catch (err) {
      console.error('Failed to update favorite:', err);
    }
  };

  const handleOoToggleStatus = async (listing) => {
    if (!listing) return;
    const isInteressant = listing.status === 'interessant';
    const nextStatus = isInteressant ? 'gelezen' : 'interessant';
    try {
      await axios.patch(`${API}/oo/listings/${listing.poiid}`, { status: nextStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setListings((prev) =>
        prev.map((item) =>
          item.poiid === listing.poiid ? { ...item, status: nextStatus } : item,
        ),
      );
      setSelectedListing((prev) =>
        prev && prev.poiid === listing.poiid ? { ...prev, status: nextStatus } : prev,
      );
      fetchStats();
    } catch (error) {
      console.error('Failed to toggle OO status:', error);
    }
   };

  const handleUpdateStatus = async () => {
    if (!selectedListing) return;
    setStatusUpdating(true);
    try {
      await axios.patch(`${API}/oo/listings/${selectedListing.poiid}`, { status: statusValue }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setListings((prev) =>
        prev.map((item) => (item.poiid === selectedListing.poiid ? { ...item, status: statusValue } : item)),
      );
      setSelectedListing({ ...selectedListing, status: statusValue });
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleMarkContacted = async (listing) => {
    try {
      const payload = { status: 'gecontacteerd' };
      await axios.patch(`${API}/oo/listings/${listing.poiid}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchListings();
      fetchStats();
    } catch (err) {
      console.error('Failed to mark contacted:', err);
    }
  };

  const geometryFeature = useMemo(() => {
    if (!selectedListing?.geometry) return null;
    if (selectedListing.geometry.type === 'Feature') {
      return selectedListing.geometry;
    }
    return {
      type: 'Feature',
      geometry: selectedListing.geometry,
      properties: {
        naam: selectedListing.naam,
      },
    };
  }, [selectedListing]);

  const streetViewLocation = useMemo(() => {
    if (!selectedListing) return null;
    const geom =
      selectedListing.geometry?.type === 'Feature'
        ? selectedListing.geometry.geometry
        : selectedListing.geometry;
    if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
      const [lon, lat] = geom.coordinates;
      if (typeof lat === 'number' && typeof lon === 'number' && Number.isFinite(lat) && Number.isFinite(lon)) {
        return { lat, lon };
      }
    }
    return null;
  }, [selectedListing]);


  const effectiveTotal = stats?.total ?? totalCount;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / PAGE_SIZE));
  const startIndex = effectiveTotal === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const endIndex = effectiveTotal === 0 ? 0 : Math.min(effectiveTotal, currentPage * PAGE_SIZE + listings.length);
  const freshnessLabel = FRESHNESS_LABELS[freshnessFilter] || FRESHNESS_LABELS.quarter;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader
        user={user}
        currentPage="oo-woningen"
        onNavigate={handleNavigate}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Ongeschikt/Onbewoonbaar</h2>
          <p className="text-gray-600">Overzicht van ongeschikte en onbewoonbare woningen (Mercator dataset)</p>
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'total' ? 'ring-2 ring-emerald-500' : ''}`}
                onClick={() => {
                  setActiveStatsTab(activeStatsTab === 'total' ? null : 'total');
                  setSelectedStatus('all');
                  setShowFavoritesOnly(false);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-100 rounded-lg">
                      <Home className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Totaal woningen ({freshnessLabel})</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'campagne' ? 'ring-2 ring-red-500' : ''}`}
                onClick={() => {
                  setActiveStatsTab(activeStatsTab === 'campagne' ? null : 'campagne');
                  setSelectedStatus('all');
                  setShowFavoritesOnly(false);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-100 rounded-lg">
                      <Mail className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Campagne</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.favorites_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'favorieten' ? 'ring-2 ring-yellow-500' : ''}`}
                onClick={() => {
                  setActiveStatsTab(activeStatsTab === 'favorieten' ? null : 'favorieten');
                  setShowFavoritesOnly(false);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <Star className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Favorieten</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.status_counts?.interessant || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'verzonden' ? 'ring-2 ring-purple-500' : ''}`}
                onClick={() => {
                  setActiveStatsTab(activeStatsTab === 'verzonden' ? null : 'verzonden');
                  setShowFavoritesOnly(false);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <Send className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Verzonden Campagnes</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.contacted_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {stats.province_counts.map((item) => (
                <Card key={item.province}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-sky-100 rounded-lg">
                        <Building className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">{item.province}</p>
                        <p className="text-xl font-semibold text-gray-900">{item.total}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {stats.unknown > 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Building className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Onbekend</p>
                        <p className="text-xl font-semibold text-gray-900">{stats.unknown}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}

        <Card className="mb-8">
          <CardContent className="pt-5">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="min-w-60">
                  <Select value={selectedProvince} onValueChange={setSelectedProvince}>
                    <SelectTrigger>
                      <SelectValue placeholder="Provincie" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((province) => (
                        <SelectItem key={province.value} value={province.value}>
                          {province.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="min-w-58">
                  <Select value={selectedGemeente} onValueChange={setSelectedGemeente}>
                    <SelectTrigger>
                      <SelectValue placeholder="Gemeente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle gemeenten</SelectItem>
                      {gemeenten.map((gemeente) => (
                        <SelectItem key={gemeente.value} value={gemeente.value}>
                          {gemeente.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="min-w-58">
                  <Input
                    type="text"
                    placeholder="Postcode"
                    value={selectedPostcode}
                    onChange={(e) => setSelectedPostcode(e.target.value)}
                  />
                </div>

                <div className="min-w-60">
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle statussen</SelectItem>
                      <SelectItem value="new">Nieuw</SelectItem>
                      <SelectItem value="interessant">Favorieten</SelectItem>
                      <SelectItem value="gelezen">Gelezen</SelectItem>
                      <SelectItem value="gecontacteerd">Gecontacteerd</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="min-w-60">
                  <Select value={freshnessFilter} onValueChange={setFreshnessFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Periode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quarter">Laatste 3 maanden</SelectItem>
                      <SelectItem value="month">Laatste maand</SelectItem>
                      <SelectItem value="week">Laatste week</SelectItem>
                      <SelectItem value="all">Alle periodes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

               {/*} <div className="flex items-center gap-2 min-w-40">
                  <Switch
                    id="favorites-only"
                    checked={showFavoritesOnly}
                    onCheckedChange={setShowFavoritesOnly}
                  />
                  <Label htmlFor="favorites-only" className="text-sm cursor-pointer whitespace-nowrap">
                    Alleen favorieten
                  </Label>
                </div>*/}
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Zoek op naam, straat of gemeente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {effectiveTotal > 0
                ? `Woningen (${startIndex}-${endIndex} van ${effectiveTotal})`
                : `Woningen (${listings.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Woningen laden...</div>
            ) : listings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Geen woningen gevonden die voldoen aan de filters.
              </div>
            ) : (
              <div className="space-y-4">
                {listings.map((listing) => (
                  <div
                    key={listing.poiid}
                    className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-colors cursor-pointer relative"
                    onClick={() => handleViewDetails(listing)}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`absolute top-3 right-3 p-1 h-8 w-8 rounded-full hover:bg-gray-100  ${listing.status === 'interessant'
                          ? 'text-yellow-500 bg-red-50'
                          : 'text-gray-400 hover:text-red-500'
                        }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOoToggleStatus(listing);
                      }}
                    >
                      <Star className="w-4 h-4" style={{ width: '30px', height: '30px' }} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      style={{ marginTop: '35px' }}
                      className={`absolute top-3 right-3 p-1 h-8 w-8 rounded-full hover:bg-gray-100  ${listing.is_favorite ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleFavorite(listing, !listing.is_favorite);
                      }}
                    >
                      <Mail className="w-4 h-4" style={{ width: '30px', height: '30px' }} />
                    </Button>




                    <div className="flex-1 pr-12">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {listing.naam || `Woning ${listing.poiid}`}
                        </h3>
                        {listing.status && <StatusBadge status={listing.status} />}
                        {listing.postbird_id && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <MailOpen className="w-3 h-3 mr-1" />
                            Gecontacteerd
                          </Badge>
                        )}
                        {listing.province && (
                          <Badge variant="outline" className="text-xs">
                            {listing.province}
                          </Badge>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {`${listing.straat || ''} ${listing.huisnummer || ''}${
                            listing.busnummer ? ` bus ${listing.busnummer}` : ''
                          }, ${listing.postcode || ''} ${listing.gemeente || ''}`}
                        </span>
                        {listing.created && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(listing.created).toLocaleDateString('nl-NL')}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          Opp. gebouw: {formatArea(listing.building_area_m2)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Home className="w-4 h-4" />
                          Opp. perceel: {formatArea(listing.parcel_area_m2)}
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-2">
            
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
              <div className="text-sm text-gray-600">
                {effectiveTotal > 0
                  ? `Resultaten ${startIndex}-${endIndex} van ${effectiveTotal}`
                  : 'Geen resultaten gevonden'}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                  disabled={currentPage === 0}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Vorige
                </Button>
                <div className="text-sm text-gray-600 min-w-[120px] text-center">
                  Pagina {Math.min(currentPage + 1, totalPages)} van {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="flex items-center gap-1"
                >
                  Volgende
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedListing && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center justify-between">
                  <span>{selectedListing.naam || `Woning ${selectedListing.poiid}`}</span>
                 {/*<Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleFavorite(selectedListing, !selectedListing.is_favorite)}
                    className={selectedListing.is_favorite ? 'text-red-500' : 'text-gray-400'}
                  >
                    <Mail
                      className={`w-4 h-4 ${
                        selectedListing.is_favorite ? 'text-red-500 bg-red-50 rounded p-0.5' : 'text-gray-400'
                      }`}
                    />
                  </Button>*/}
                </SheetTitle>
                <SheetDescription>POIID: {selectedListing.poiid}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status and Actions */}
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={` p-1 h-8 w-8 rounded-full hover:bg-gray-100 ${
                      selectedListing.status=='interessant' ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                    }`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOoToggleStatus(selectedListing);
                        
                    }}
                    >
                    <Star className={`w-5 h-5 ${selectedListing.status=='interessant' ? 'text-yellow-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px',marginRight: '10px' }} />                              
                  </Button>
                  <Button
                      variant="ghost"
                      size="sm"
                      /*onClick={() => setIsFavorite(!isFavorite)}*/
                      onClick={() => {handleToggleFavorite(selectedListing, !selectedListing.is_favorite);}}
                      className={selectedListing.is_favorite ? "text-red-500" : "text-gray-400"}
                    >
                      <Mail className={`w-4 h-4 ${selectedListing.is_favorite  ? 'text-red-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px' }} />
                    </Button>                  
                </div>
                <Separator />
                </div>
              

              <div className="mt-6 space-y-6">
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>
                      {selectedListing.straat} {selectedListing.huisnummer}
                      {selectedListing.busnummer ? ` bus ${selectedListing.busnummer}` : ''}
                    </span>
                  </div>
                  <div>{selectedListing.postcode} {selectedListing.gemeente}</div>
                  {selectedListing.province && (
                    <Badge variant="outline">{selectedListing.province}</Badge>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-600">Oppervlakte perceel</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">
                        {formatArea(selectedListing.parcel_area_m2)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-600">Oppervlakte gebouw</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">
                        {formatArea(selectedListing.building_area_m2)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">Locatie</h3>
                    <Badge variant="secondary">GRB kaart</Badge>
                  </div>
                  <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200">
                    <LeafletMap geometry={geometryFeature} />
                  </div>
                </div>

                {streetViewLocation && (
                  <div className="space-y-3">
                    <Button
                      asChild
                      variant="outline"
                      className="w-full justify-center gap-2"
                    >
                      <a
                        href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${streetViewLocation.lat},${streetViewLocation.lon}&layer=c`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                         <ExternalLink className="w-4 h-4" />
                         <span>Bekijk in Google Street View</span>
                      </a>
                    </Button>
                  </div>
                )}

                <Separator />

                {/*<div className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={statusValue} onValueChange={setStatusValue}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Nieuw</SelectItem>
                        <SelectItem value="interessant">Favorieten</SelectItem>
                        <SelectItem value="gelezen">Gelezen</SelectItem>
                        <SelectItem value="gecontacteerd">Gecontacteerd</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={statusUpdating}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {statusUpdating ? 'Opslaan...' : 'Status opslaan'}
                  </Button>
                  {!selectedListing.postbird_id && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleMarkContacted(selectedListing)}
                    >
                      Markeer als gecontacteerd
                    </Button>
                  )}
                </div>*/}

                {selectedListing.omschrijving && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg">Beschrijving</h3>
                    <Textarea readOnly value={selectedListing.omschrijving} className="min-h-[120px]" />
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default OOWoningenModule;
